import type {
  EvaluationMethodConfig, EvaluationMethodId, EvaluationRun, EvaluationRunStatus,
} from './sweetSpotEvaluationEngine';

const DATABASE_NAME = 'fracsight-sweetspot-evaluations';
const DATABASE_VERSION = 1;
const RUN_STORE = 'evaluationRuns';
const ACTIVE_RUN_PREFIX = 'fracsight:sweetspot-active-run:';
const METHOD_CONFIG_PREFIX = 'fracsight:sweetspot-method-config:';

export class EvaluationStorageError extends Error {
  constructor(
    message: string,
    public readonly code: 'UNAVAILABLE' | 'QUOTA' | 'CORRUPT' | 'NOT_FOUND' | 'UNKNOWN',
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'EvaluationStorageError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

export function isEvaluationRun(value: unknown): value is EvaluationRun {
  if (!isRecord(value)) return false;
  const validConfig = isRecord(value.config)
    && isRecord(value.config.parameters)
    && Array.isArray(value.config.parameters.rq)
    && Array.isArray(value.config.parameters.cq)
    && isRecord(value.config.anchors)
    && isRecord(value.config.gradeThresholds);
  const validPoints = Array.isArray(value.points) && value.points.every((point) => isRecord(point)
    && typeof point.depth === 'number' && Number.isFinite(point.depth)
    && (point.rq === null || (typeof point.rq === 'number' && Number.isFinite(point.rq)))
    && (point.cq === null || (typeof point.cq === 'number' && Number.isFinite(point.cq)))
    && (point.ssi === null || (typeof point.ssi === 'number' && Number.isFinite(point.ssi))));
  const validInputSnapshot = isRecord(value.inputSnapshot)
    && (value.inputSnapshot.inputSignature === undefined || typeof value.inputSnapshot.inputSignature === 'string')
    && (value.inputSnapshot.configSignature === undefined || typeof value.inputSnapshot.configSignature === 'string');
  return typeof value.runId === 'string'
    && typeof value.projectId === 'number'
    && typeof value.wellId === 'string'
    && typeof value.methodKey === 'string'
    && typeof value.methodId === 'string'
    && typeof value.methodVersion === 'string'
    && typeof value.definitionHash === 'string'
    && typeof value.createdAt === 'string'
    && typeof value.updatedAt === 'string'
    && validPoints
    && validConfig
    && isRecord(value.quality)
    && validInputSnapshot;
}

function classifyStorageError(error: unknown, fallback: string): EvaluationStorageError {
  if (error instanceof EvaluationStorageError) return error;
  const name = isRecord(error) && typeof error.name === 'string' ? error.name : '';
  if (name === 'QuotaExceededError') return new EvaluationStorageError('浏览器存储容量不足，运行未保存。', 'QUOTA', error);
  if (name === 'InvalidStateError' || name === 'SecurityError' || name === 'NotSupportedError') {
    return new EvaluationStorageError('IndexedDB不可用，未静默降级；请检查浏览器隐私或存储设置。', 'UNAVAILABLE', error);
  }
  return new EvaluationStorageError(fallback, 'UNKNOWN', error);
}

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new EvaluationStorageError('当前环境不支持IndexedDB。', 'UNAVAILABLE'));
  }
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    } catch (error) {
      reject(classifyStorageError(error, '无法打开评价版本库。'));
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(RUN_STORE)) {
        const store = db.createObjectStore(RUN_STORE, { keyPath: 'runId' });
        store.createIndex('projectId', 'projectId', { unique: false });
        store.createIndex('projectCreatedAt', ['projectId', 'createdAt'], { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(classifyStorageError(request.error, '无法打开评价版本库。'));
    request.onblocked = () => reject(new EvaluationStorageError('版本库升级被其他页面阻止，请关闭其他标签页后重试。', 'UNAVAILABLE'));
  });
}

function requestResult<T>(request: IDBRequest<T>, fallback: string): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(classifyStorageError(request.error, fallback));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
  fallback: string,
): Promise<T> {
  const db = await openDatabase();
  try {
    const transaction = db.transaction(RUN_STORE, mode);
    const result = await requestResult(action(transaction.objectStore(RUN_STORE)), fallback);
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(classifyStorageError(transaction.error, fallback));
      transaction.onerror = () => reject(classifyStorageError(transaction.error, fallback));
    });
    return result;
  } finally {
    db.close();
  }
}

export async function saveEvaluationRun(run: EvaluationRun): Promise<EvaluationRun> {
  if (!isEvaluationRun(run)) throw new EvaluationStorageError('评价运行结构无效，未保存。', 'CORRUPT');
  const existing = await getEvaluationRun(run.runId);
  if (existing) throw new EvaluationStorageError('该runId已存在；保存新版本必须使用新的runId。', 'CORRUPT');
  const saved: EvaluationRun = { ...run, status: 'saved', updatedAt: new Date().toISOString() };
  await withStore('readwrite', (store) => store.add(saved), '保存评价运行失败。');
  return saved;
}

export async function updateEvaluationRunStatus(
  runId: string,
  status: EvaluationRunStatus,
  patch: Pick<EvaluationRun, 'syncError' | 'activatedAt'> = {},
): Promise<EvaluationRun> {
  const run = await getEvaluationRun(runId);
  if (!run) throw new EvaluationStorageError('找不到要更新的评价运行。', 'NOT_FOUND');
  const next: EvaluationRun = {
    ...run, ...patch, status, updatedAt: new Date().toISOString(),
  };
  await withStore('readwrite', (store) => store.put(next), '更新评价运行状态失败。');
  return next;
}

export async function getEvaluationRun(runId: string): Promise<EvaluationRun | undefined> {
  const value = await withStore<unknown>('readonly', (store) => store.get(runId), '读取评价运行失败。');
  if (value === undefined) return undefined;
  if (!isEvaluationRun(value)) throw new EvaluationStorageError('评价版本数据损坏，无法读取。', 'CORRUPT');
  return value;
}

export async function listEvaluationRuns(projectId: number): Promise<EvaluationRun[]> {
  const db = await openDatabase();
  try {
    const transaction = db.transaction(RUN_STORE, 'readonly');
    const index = transaction.objectStore(RUN_STORE).index('projectId');
    const values = await requestResult(index.getAll(IDBKeyRange.only(projectId)), '读取评价历史失败。');
    if (!values.every(isEvaluationRun)) throw new EvaluationStorageError('评价历史中存在损坏数据。', 'CORRUPT');
    return values.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } finally {
    db.close();
  }
}

export function getActiveRunId(projectId: number): string | undefined {
  try {
    return localStorage.getItem(`${ACTIVE_RUN_PREFIX}${projectId}`) || undefined;
  } catch (error) {
    throw classifyStorageError(error, '无法读取当前活动版本指针。');
  }
}

export function setActiveRunId(projectId: number, runId: string): void {
  try {
    localStorage.setItem(`${ACTIVE_RUN_PREFIX}${projectId}`, runId);
  } catch (error) {
    throw classifyStorageError(error, '后端已同步，但无法更新本地活动版本指针；请不要关闭页面并立即重试。');
  }
}

export async function loadActiveEvaluationRun(projectId: number): Promise<EvaluationRun | undefined> {
  const runId = getActiveRunId(projectId);
  return runId ? getEvaluationRun(runId) : undefined;
}

export function saveMethodConfig(
  projectId: number,
  methodId: EvaluationMethodId,
  config: EvaluationMethodConfig,
): boolean {
  try {
    localStorage.setItem(`${METHOD_CONFIG_PREFIX}${projectId}:${methodId}`, JSON.stringify(config));
    return true;
  } catch {
    return false;
  }
}

export function loadMethodConfig(
  projectId: number | undefined,
  methodId: EvaluationMethodId,
): EvaluationMethodConfig | undefined {
  if (!projectId) return undefined;
  const storageKey = `${METHOD_CONFIG_PREFIX}${projectId}:${methodId}`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || !isRecord(value.parameters) || !Array.isArray(value.parameters.rq)
      || !Array.isArray(value.parameters.cq) || !isRecord(value.anchors)
      || !isRecord(value.gradeThresholds)
      || (value.weightStrategy !== 'fixed' && value.weightStrategy !== 'user' && value.weightStrategy !== 'ahp')
      || (value.combination !== 'arithmetic_mean' && value.combination !== 'geometric_mean')
      || typeof value.allowLegacySingleGroupActivation !== 'boolean') {
      localStorage.removeItem(storageKey);
      return undefined;
    }
    return value as unknown as EvaluationMethodConfig;
  } catch {
    try { localStorage.removeItem(storageKey); } catch { /* storage remains unavailable */ }
    return undefined;
  }
}
